// The canonical order-package basis — ONE purchase_options row represents ONE
// legally orderable package, and every fact on the row describes that same
// package: purchase_unit, package_price, qty_per_package, the pack
// description, the inner-pack structure, MOQ, order increment, the comparison
// quantity, and the quantity written to a PO or shopping list.
//
// The bug this module exists to prevent was visible on one Savings Review
// card: "Need 3 rolls · Buy 3 bags · 4 bags × 12 rolls · $28.13 · $0.20/roll".
// That row's order math treated one "bag" as one roll (qty_per_package = 1),
// its price was a prorated quarter of the case price, its pack description
// described the whole case, and its comparable price divided by the case's 48
// rolls. Four package definitions on one row, and no reader ever checked that
// they agreed.
//
// So this module owns three things, for BOTH runtimes (the browser imports
// this file directly — the vendorState.mjs pattern; do not fork a copy):
//
//   resolveOrderPackageBasis()   what the one orderable package IS, resolved
//                                from stored facts by one precedence chain
//   packageBasisFingerprint()    a stable identifier of the package-defining
//                                facts, stored with every price comparison so
//                                a comparison computed against yesterday's
//                                package can never decorate today's
//   validatePackageConsistency() whether the row's stored facts all describe
//                                the same package — and the two readiness
//                                states that gate everything downstream:
//                                order-ready and comparison-ready
//
// Deliberately NO second pack-text parser here: parsing belongs to
// purchaseConversion/packText. Stored structured columns and snapshots remain
// the ordinary evidence. The one need-time question that must consult the
// conversion owner is purchase-unit identity (one ordered CASE is one needed
// CASE); reusing that owner keeps its divided-case refusal intact. Nothing
// here throws on bad input, and an unresolved quantity is null — never 0.

// The container vocabulary distinguishes piece nouns ("bun") from shipping
// wrappers ("case"). The purchase-conversion import is the authoritative
// reader for the single live identity question described above.
import { isShippingContainer } from "./packText.mjs";
import { packageToCountUnitQty } from "./purchaseConversion.mjs";

// ── Constants ────────────────────────────────────────────────────────────────

export const PACKAGE_BASIS_VERSION = 1;

// Same-unit quantities must agree essentially exactly — they are the same
// stored fact written twice. Cross-unit checks tolerate the numeric(12,6)
// column rounding both engines write through.
export const QTY_TOLERANCE_EXACT = 1e-6;
export const QTY_TOLERANCE_CONVERTED = 5e-4;

// Stored costs are numeric(12,6); a derived cost that drifts more than half a
// percent from price ÷ quantity was not computed from this package.
export const NORMALIZED_COST_TOLERANCE = 0.005;

// Below this confidence an AI-estimated comparison may be displayed but never
// crowns a winner. Mirrors priceComparison's constant; redeclared here so this
// module stays import-cycle-free (priceComparison imports packageBasis).
export const BASIS_AI_CONFIDENCE_THRESHOLD = 0.65;

export const PACKAGE_BASIS_WARNINGS = Object.freeze({
  PACKAGE_BASIS_MISMATCH: "package_basis_mismatch",
  UNRESOLVED_PACKAGE_QUANTITY: "unresolved_package_quantity",
  MISSING_PURCHASE_UNIT: "missing_purchase_unit",
  MISSING_PRICE_BASIS: "missing_price_basis",
  INVALID_MOQ: "invalid_moq",
  INVALID_ORDER_INCREMENT: "invalid_order_increment",
  STORED_QTY_STALE: "stored_qty_stale",
  STRUCTURED_FIELDS_STALE: "structured_fields_stale",
  NORMALIZED_COST_MISMATCH: "normalized_cost_mismatch",
  QTY_MATCHES_INNER_PACK_COUNT: "qty_matches_inner_pack_count",
  COMPARISON_STALE: "comparison_stale",
  COMPARISON_QUANTITY_CONFLICT: "comparison_quantity_conflict",
  COMPARISON_UNVALIDATED: "comparison_unvalidated",
  COMPARISON_LOW_CONFIDENCE: "comparison_low_confidence",
  UNLABELLED_QUANTITY: "unlabelled_quantity",
  NEED_UNIT_INCOMPATIBLE: "need_unit_incompatible_with_package_basis",
  PURCHASE_UNIT_PACKAGE_CONFLICT: "purchase_unit_package_conflict",
  SEMANTIC_REVIEW_REQUIRED: "semantic_package_review_required",
});

// ── Small shared helpers ─────────────────────────────────────────────────────

const text = (v) => (v == null ? "" : String(v).trim());

const positive = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const round6 = (v) => Math.round(v * 1e6) / 1e6;

const fold = (v) =>
  text(v)
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, " ")
    .trim();

const sameQty = (a, b, tolerance = QTY_TOLERANCE_EXACT) => {
  const x = positive(a);
  const y = positive(b);
  if (x === null && y === null) return true;
  if (x === null || y === null) return false;
  return Math.abs(x - y) <= Math.max(tolerance, Math.max(x, y) * tolerance);
};

const measurementsAgree = (aQty, aUnit, bQty, bUnit) => {
  if (!text(aUnit) && !text(bUnit)) {
    return sameQty(aQty, bQty, QTY_TOLERANCE_EXACT);
  }
  const converted = convertBasisQuantity(aQty, aUnit, bUnit);
  if (converted === null) return null;
  return sameQty(converted, bQty, QTY_TOLERANCE_CONVERTED);
};

// ── Unit identity for stored-data validation ─────────────────────────────────
//
// The comparison engine writes its snapshots in a small set of practical
// display units, and inventory items count in a somewhat wider set. Both are
// known unit words with exact factors, so a legacy comparison snapshot can be
// validated against the order package deterministically wherever both units
// live in this table. Anything outside it (an AI-bridged piece↔weight
// comparison, a container word) cannot be proven from stored data and is
// reported as unvalidated rather than guessed.

const MASS_FACTORS = Object.freeze({
  g: 1, gram: 1, grams: 1,
  kg: 1000, kilogram: 1000, kilograms: 1000,
  oz: 28.349523125, ounce: 28.349523125, ounces: 28.349523125,
  lb: 453.59237, lbs: 453.59237, pound: 453.59237, pounds: 453.59237,
});

const VOLUME_FACTORS = Object.freeze({
  ml: 1, milliliter: 1, milliliters: 1, millilitre: 1, millilitres: 1,
  l: 1000, liter: 1000, liters: 1000, litre: 1000, litres: 1000,
  "fl oz": 29.5735295625, floz: 29.5735295625,
  "fluid ounce": 29.5735295625, "fluid ounces": 29.5735295625,
  qt: 946.352946, qts: 946.352946, quart: 946.352946, quarts: 946.352946,
  gal: 3785.411784, gallon: 3785.411784, gallons: 3785.411784,
});

// Generic count words are mutually interchangeable ("each" of a roll-counted
// item IS a roll). A specific piece noun equals itself, singular or plural.
const COUNT_SYNONYMS = new Set(["each", "ea", "unit", "units", "count", "ct", "piece", "pieces", "pc", "pcs"]);
const CONTAINER_COUNT_SYNONYMS = Object.freeze({
  pk: "pack", pks: "pack", pkg: "pack", pkgs: "pack", pack: "pack", packs: "pack",
  cs: "case", case: "case", cases: "case",
  bib: "bib", bibs: "bib",
  "bag in box": "bib", "bags in box": "bib", "bag in boxes": "bib",
});

const singular = (word) => (word.endsWith("s") && word.length > 3 ? word.slice(0, -1) : word);

/**
 * Are two unit words the SAME physical unit, however they are spelled?
 *
 * Spelling equality was the old test and it is wrong in both directions:
 * "gal" and "gallon" are one unit written twice, while "oz" (mass) and
 * "floz" (volume) are two different quantities that merely look alike.
 * Compare the descriptor — family plus factor — so a stored snapshot written
 * as "gallon" still answers a group asking for "gal", and a weight ounce
 * never satisfies a fluid one.
 *
 * Count units compare by key, not factor: every count word has factor 1, but
 * a "bottle" is not an "each" of a case-counted item.
 */
export function sameComparisonUnit(left, right) {
  const a = unitDescriptor(left);
  const b = unitDescriptor(right);
  if (!a || !b) return false;
  if (a.family !== b.family) return false;
  if (a.family === "count") return a.key === b.key;
  return a.factor === b.factor;
}

/** The measurement family ('mass' | 'volume' | 'count') of a unit word. */
export function unitFamilyOf(unitRaw) {
  return unitDescriptor(unitRaw)?.family ?? null;
}

function unitDescriptor(unitRaw) {
  const key = fold(unitRaw);
  if (!key) return null;
  if (MASS_FACTORS[key] != null) return { family: "mass", factor: MASS_FACTORS[key], key };
  if (VOLUME_FACTORS[key] != null) return { family: "volume", factor: VOLUME_FACTORS[key], key };
  if (COUNT_SYNONYMS.has(key)) return { family: "count", factor: 1, key: "each" };
  if (CONTAINER_COUNT_SYNONYMS[key]) {
    return { family: "count", factor: 1, key: CONTAINER_COUNT_SYNONYMS[key] };
  }
  // An unrecognized word is a piece/container noun — countable, identity by
  // the (singularized) word itself.
  return { family: "count", factor: 1, key: singular(key) };
}

/**
 * Converts `qty` of `fromUnit` into `toUnit` using only the exact factors
 * above. Returns null when the conversion cannot be proven from stored data —
 * different families, or piece nouns that do not name the same thing.
 */
export function convertBasisQuantity(qty, fromUnit, toUnit) {
  const amount = positive(qty);
  if (amount === null) return null;
  const from = unitDescriptor(fromUnit);
  const to = unitDescriptor(toUnit);
  if (!from || !to) return null;
  if (from.family !== to.family) return null;
  if (from.family === "count") {
    if (from.key === to.key) return amount;
    // Any specific count IS that many eaches (24 bottles = 24 each)…
    if (to.key === "each") return amount;
    // …and "each" bridges to a piece noun (each of a bun-counted item IS a
    // bun) — but never to a SHIPPING WRAPPER: 12 anonymous inner pieces are
    // not 12 cases/bottles/bags, and reading them as one was how a 12-piece
    // count once claimed to be 12 cases of the item.
    if (from.key === "each" && !isShippingContainer(to.key)) return amount;
    return null;
  }
  return (amount * from.factor) / to.factor;
}

// ── Stored facts, read once ──────────────────────────────────────────────────

/**
 * The human-confirmed package interpretation, if one is stored. Mirrors
 * packageLadder.storedInterpretation's acceptance rule (source 'confirmed' or
 * a confirmed_at stamp; total_package_quantity stands in only when its unit
 * matches the count unit) without importing the parser.
 */
export function confirmedInterpretationQty(option) {
  const snap = option?.conversion_snapshot?.package_interpretation;
  if (!snap || typeof snap !== "object") return null;
  if (snap.source !== "confirmed" && !snap.confirmed_at) return null;
  let total = positive(snap.quantity_in_item_count_unit);
  if (total === null) {
    const packageUnit = fold(snap.total_package_unit);
    const countUnit = fold(snap.item_count_unit);
    if (packageUnit && countUnit && packageUnit === countUnit) {
      total = positive(snap.total_package_quantity);
    }
  }
  if (total === null) return null;
  return { quantity: total, unit: snap.item_count_unit ?? null };
}

function structuredQtyOf(option) {
  const count = positive(option?.inner_pack_count);
  const size = positive(option?.inner_pack_size);
  if (count === null || size === null) return null;
  return { quantity: round6(count * size), unit: option?.inner_pack_size_unit ?? null, count, size };
}

/** The stored price_comparison snapshot's package-defining facts, raw. */
export function storedComparisonFacts(option) {
  const snap = option?.conversion_snapshot?.price_comparison;
  if (!snap || typeof snap !== "object") return null;
  const quantity = positive(snap.quantity_per_purchase_package);
  if (quantity === null) return null;
  return {
    quantity,
    relationshipUnit: snap.relationship_unit ?? null,
    source: snap.source ?? null,
    confidence: Number.isFinite(Number(snap.confidence)) ? Number(snap.confidence) : null,
    fingerprint: text(snap.package_basis_fingerprint) || null,
    version: Number(snap.version) || null,
  };
}

const CONFIRMED_MEASUREMENT_SOURCES = new Set([
  "confirmed", "manual", "manual_override", "user", "human_confirmed",
]);

const ORDERABLE_MEASUREMENT_SOURCES = new Set([
  ...CONFIRMED_MEASUREMENT_SOURCES,
  "vendor_pack", "structured", "deterministic", "stored_qty",
]);

// jsonb booleans arrive as real booleans, but a defensive string form has
// been seen in hand-repaired rows; both spellings of "not safe" count.
const explicitlyUnsafe = (v) => v === false || v === "false";

/**
 * True when the option's qty_per_package is the `fallback_each` DEFAULT — the
 * conversion could not read the package and counted "1 package as 1 each"
 * with no stated case quantity behind it. Such a 1 is a counting convenience;
 * treating it as a package fact is how a 5.5-EA need once bought six $132
 * cases. Detected from stored provenance only (this module never re-parses):
 * the conversion writer records method + evidence, and legacy rows are
 * recognized by method 'fallback_each' with no caseQuantity in the recorded
 * inputs.
 *
 * EXPLICIT unsafe provenance outranks every method-name heuristic: a row
 * whose provenance or snapshot says `order_safe: false` or
 * `evidence: 'fallback_default'` is unsafe WHATEVER its method says. The
 * menu-starter AI passes used to rewrite `method` to 'ai' while spreading the
 * old fallback provenance underneath — Sprite/Ginger Ale BIB rows carried
 * {method:'ai', evidence:'fallback_default', order_safe:false, qty:1} and
 * sailed past a detector that only knew the method name 'fallback_each'.
 * (A later human confirmation or deterministic re-conversion replaces the
 * provenance wholesale, so this never overrules newer, stronger evidence.)
 */
export function storedQtyIsFallbackDefault(option) {
  const provenance = option?.conversion_provenance;
  const snapshot = option?.conversion_snapshot;
  if (explicitlyUnsafe(provenance?.order_safe)) return true;
  if (text(provenance?.evidence).toLowerCase() === "fallback_default") return true;
  if (snapshot && typeof snapshot === "object") {
    if (explicitlyUnsafe(snapshot.order_safe)) return true;
    if (text(snapshot.evidence).toLowerCase() === "fallback_default") return true;
  }
  const method = text(provenance?.method).toLowerCase();
  if (method !== "fallback_each") return false;
  const evidence = text(provenance?.evidence).toLowerCase();
  // The CURRENT writer records this judgment explicitly: the pack text carried
  // no legible count under the current parser and the extraction stated a
  // real case quantity. Trusted.
  if (evidence === "stored_case_quantity") return false;
  if (evidence) return true;
  // Legacy row (no evidence field). A fallback WITH pack text means the text
  // could not be read by the parser of its day — "12/12–16 OZ AVG" fell to 1
  // while stating 12. The current parser must re-derive it (audit repair)
  // before the stored number may order; a stated case quantity does not
  // launder an unread pack. Only a row with NO pack text at all may lean on
  // its recorded case quantity.
  if (text(option?.pack_description)) return true;
  const inputs = option?.conversion_snapshot?.inputs;
  if (inputs && typeof inputs === "object") {
    return positive(inputs.caseQuantity ?? inputs.case_quantity) === null;
  }
  // Provenance says fallback and nothing records a stated case quantity.
  return true;
}

const normalizedMeasurement = (raw, defaults = {}) => {
  if (!raw || typeof raw !== "object") return null;
  const quantity = positive(raw.quantity ?? raw.qty ?? raw.quantity_per_package);
  const unit = text(raw.unit ?? raw.quantity_unit ?? defaults.unit) || null;
  if (quantity === null || !unit) return null;
  const source = text(raw.source ?? defaults.source).toLowerCase() || "unresolved";
  const confidenceRaw = Number(raw.confidence ?? defaults.confidence);
  const confirmed = raw.confirmed === true
    || raw.verified === true
    || CONFIRMED_MEASUREMENT_SOURCES.has(source);
  return {
    quantity,
    unit,
    role: text(raw.role ?? defaults.role) || "contents",
    source,
    confirmed,
    confidence: Number.isFinite(confidenceRaw)
      ? Math.max(0, Math.min(1, confidenceRaw))
      : confirmed ? 1 : (defaults.confidence ?? null),
  };
};

/**
 * Every independently stored measurement of ONE orderable package. A case may
 * legitimately be both 50 LB and 80 EA; keeping those measurements separate
 * is what lets price comparison use LB while order math uses a confirmed EA
 * bridge. AI estimates are retained for display but are not order-authorizing.
 */
export function extractPackageMeasurements(option, { item = null } = {}) {
  const rows = [];
  const add = (raw, defaults = {}) => {
    const measurement = normalizedMeasurement(raw, defaults);
    if (!measurement) return;
    const key = `${round6(measurement.quantity)}|${fold(measurement.unit)}|${measurement.role}`;
    const existing = rows.findIndex((row) => row.key === key);
    const withKey = { ...measurement, key };
    if (existing < 0) rows.push(withKey);
    else if (!rows[existing].confirmed && withKey.confirmed) rows[existing] = withKey;
  };

  const snapshot = option?.conversion_snapshot || {};
  const stored = Array.isArray(snapshot.package_measurements)
    ? snapshot.package_measurements
    : Array.isArray(snapshot.quantities) ? snapshot.quantities : [];
  stored.forEach((measurement) => add(measurement));

  const interpretation = snapshot.package_interpretation;
  if (interpretation && typeof interpretation === "object"
      && (interpretation.source === "confirmed" || interpretation.confirmed_at)) {
    add({
      quantity: interpretation.quantity_in_item_count_unit
        ?? interpretation.total_package_quantity,
      unit: interpretation.item_count_unit ?? interpretation.total_package_unit,
      role: "inventory_bridge",
      source: "confirmed",
      confirmed: true,
      confidence: 1,
    });
  }

  const structured = structuredQtyOf(option);
  if (structured) {
    add({
      quantity: structured.quantity,
      unit: structured.unit ?? option?.normalized_count_unit ?? item?.unit,
      role: "vendor_pack",
      source: "structured",
      confirmed: interpretation?.requires_review !== true,
      confidence: 0.9,
    });
  }

  const storedQty = positive(option?.qty_per_package);
  if (storedQty !== null) {
    const fallbackDefault = storedQtyIsFallbackDefault(option);
    add({
      quantity: storedQty,
      unit: option?.normalized_count_unit ?? item?.unit,
      role: "inventory_quantity",
      // A defaulted fallback 1 is retained for display but carries a source
      // outside the order-authorizing set.
      source: fallbackDefault ? "fallback" : "stored_qty",
      confirmed: false,
      confidence: fallbackDefault ? 0.2 : 0.6,
    });
  }

  const comparison = storedComparisonFacts(option);
  if (comparison) {
    add({
      quantity: comparison.quantity,
      unit: comparison.relationshipUnit,
      role: "comparison",
      source: comparison.source,
      confirmed: comparison.source === "manual_override",
      confidence: comparison.confidence,
    });
  }

  return rows.map(({ key: _key, ...measurement }) => measurement);
}

const sameUnitIdentity = (a, b) => {
  const from = unitDescriptor(a);
  const to = unitDescriptor(b);
  return !!from && !!to && from.family === to.family && from.key === to.key;
};

/**
 * Does the vendor's legal order unit itself satisfy this container-unit need?
 *
 * The import conversion is computed against the inventory item's unit at the
 * time of import. A later PAR/count can legitimately be in the orderable
 * container instead (the live Orange Juice row was imported in EACH but
 * needed in CASE). Re-running only this identity question through the ONE
 * purchase conversion owner recovers the invariant: one ordered CASE is one
 * needed CASE, one ordered BIB is one needed BIB.
 *
 * The converter also owns the crucial refusal: purchase_unit='bottle' beside
 * "24 × 6.8 oz bottles" is a divided-case-price signature, not one bottle.
 * Callers receive `ambiguous:true` so they cannot fall through to the stored
 * 24 and silently authorize the disputed package.
 */
export function purchaseUnitIdentityForNeed(option, needUnit) {
  const purchaseUnit = text(option?.purchase_unit) || null;
  const requestedUnit = text(needUnit) || null;
  const requested = unitDescriptor(requestedUnit);
  if (!purchaseUnit || !requestedUnit
      || requested?.family !== "count"
      || !isShippingContainer(requested.key)
      || !sameUnitIdentity(purchaseUnit, requestedUnit)) return null;

  const conversion = packageToCountUnitQty({
    packDescription: option?.pack_description,
    purchaseUnit,
    countUnit: requestedUnit,
  });
  if (conversion?.status === "valid"
      && conversion?.provenance?.method === "container_identity"
      && sameQty(conversion.qty, 1)) {
    return {
      compatible: true,
      quantity: 1,
      unit: requestedUnit,
      source: "purchase_unit_identity",
      confidence: 1,
      ambiguous: false,
    };
  }
  return {
    compatible: false,
    quantity: null,
    unit: requestedUnit,
    source: "unresolved",
    confidence: 0,
    ambiguous: conversion?.reason === "ambiguous_package",
  };
}

// Ratios agree when they describe the same physical amount to within ordinary
// stored-column rounding; a real disagreement is a package-definition conflict.
const RATIO_TOLERANCE = 5e-3;

// A bridge may be built only from measurements that are exact and
// source-backed: no AI estimates, no fallback defaults, and no approximate
// (catch-weight/average) sizes — those are exactly the estimated bridges
// requirement 9 keeps behind a human.
const bridgeUsableMeasurement = (measurement) => {
  if (!measurement) return false;
  if (measurement.source === "ai" || measurement.source === "ai_estimate" || measurement.source === "fallback") return false;
  if (!measurement.confirmed && !ORDERABLE_MEASUREMENT_SOURCES.has(measurement.source)) return false;
  const confidence = Number(measurement.confidence);
  if (!measurement.confirmed && Number.isFinite(confidence) && confidence < 0.8) return false;
  return true;
};

// Two containers wearing one noun: within this tolerance a candidate's
// per-container contents match the item's own definition of that container;
// beyond it they are physically different objects ("can" of 8.4 fl oz vs the
// item's 46 fl oz can) and the word alone must not equate them.
const CONTAINER_SIZE_TOLERANCE = 0.02;

/**
 * Does a container-count reading physically CONTRADICT the item's own
 * definition of that count unit? A package counted as `countQty` of the need
 * unit implies each one holds package-contents ÷ countQty; when the item's
 * bridge says one count unit holds a materially different amount, the two
 * merely share a word. Verifiable only when the package carries an exact
 * physical measurement in a bridge family; with no comparable measurement the
 * stated count stands (nothing contradicts it).
 */
const containerCountContradictsBridge = ({ countQty, measurements, needBridge }) => {
  const count = positive(countQty);
  if (!count || !needBridge || !Array.isArray(needBridge.ratios)) return false;
  for (const ratio of needBridge.ratios) {
    if (!positive(ratio?.quantity) || !text(ratio?.unit)) continue;
    for (const measurement of measurements || []) {
      if (measurement.role === "comparison"
        && measurement.source !== "deterministic"
        && measurement.source !== "manual_override") continue;
      if (!bridgeUsableMeasurement(measurement)) continue;
      const packageInBridgeUnit = convertBasisQuantity(measurement.quantity, measurement.unit, ratio.unit);
      if (packageInBridgeUnit === null) continue;
      const impliedPerCount = packageInBridgeUnit / count;
      return !sameQty(impliedPerCount, ratio.quantity, CONTAINER_SIZE_TOLERANCE);
    }
  }
  return false;
};

/**
 * What one COUNT UNIT of this item physically contains, derived from the
 * item's own purchase options — the need side of requirement 7's chain:
 *
 *   inventory shortage → count-unit physical quantity → comparison unit
 *   → vendor package quantity in the same unit → packagesRequired
 *
 * An item counted in a specific container ("case", "bottle", "can", "BIB")
 * defines that container by its own reference packages: a cranberry item
 * whose case is "12 × 32 oz" makes one case 384 fl oz, so a need of 0.8 case
 * IS 307.2 fl oz, and a 192 fl oz Costco package covers it in
 * ceil(307.2 / 192) = 2 packages — without ever pretending Costco's package
 * is "0.5 of a case" by container arithmetic alone.
 *
 * Derivation is deterministic and conservative:
 *   - only the item's OWN active options contribute (a related item's
 *     package does not define this item's counting unit);
 *   - the option's count-unit quantity must itself be order-authorizing
 *     (confirmed / structured / a non-fallback stored qty);
 *   - only exact, source-backed physical measurements count (no AI
 *     estimates, no approximate catch-weight sizes);
 *   - within a physical family, every contributing option must agree to
 *     RATIO_TOLERANCE — a disagreement drops that family entirely, because
 *     conflicting package definitions are a human question;
 *   - generic count units (each/ct/pc) get NO bridge: "one each = N oz" is
 *     the piece↔weight bridge that only a human may establish.
 *
 * @returns {{ countUnit: string, ratios: Array<{family: string,
 *   quantity: number, unit: string, confirmed: boolean, confidence: number,
 *   optionIds: string[]}> } | null}
 */
export function itemCountUnitPhysicalBasis({ item = null, options = [] } = {}) {
  const countUnit = text(item?.unit ?? item?.count_unit) || null;
  if (!countUnit) return null;
  const countDesc = unitDescriptor(countUnit);
  // Only a NAMED container/piece unit is defined by the item's own packages.
  // Measurable units need no bridge, and "each" must never acquire a size.
  if (!countDesc || countDesc.family !== "count" || countDesc.key === "each") return null;

  const itemId = item?.id != null ? String(item.id) : null;
  const byFamily = new Map();
  for (const option of Array.isArray(options) ? options : []) {
    if (!option) continue;
    if (option.status != null && option.status !== "active") continue;
    if (itemId != null && option.inventory_item_id != null
      && String(option.inventory_item_id) !== itemId) continue;
    const basis = resolveOrderPackageBasis(option, { item });
    const countQty = basis.quantityInInventoryUnit;
    if (countQty === null || basis.source === "unresolved") continue;
    if (!sameUnitIdentity(basis.inventoryUnit, countUnit)) continue;
    if (basis.source === "stored_qty"
      && (basis.requiresReview || storedQtyIsFallbackDefault(option))) continue;
    // Definition strength. A human confirmation defines the unit outright; a
    // container_identity package (the vendor sells THE count-unit container —
    // "46 oz can" bought by the can) is the strongest deterministic
    // statement of what one of it physically holds; every other option
    // merely counts containers of its OWN size, which defines the unit only
    // when nothing stronger exists ("24 × 6.8 oz bottles" as the item's one
    // reference package). Without the tiers, a 12 × 8.4 oz alternative
    // would "disagree" with the item's real 46 oz can and dissolve the
    // definition it should be measured against.
    const method = text(option?.conversion_provenance?.method);
    const tier = basis.source === "confirmed" ? 1 : method === "container_identity" ? 2 : 3;
    for (const measurement of basis.measurements || []) {
      // A comparison row is a stored parse too — but only the deterministic
      // or human-entered kind; an AI-estimated comparison estimates.
      if (measurement.role === "comparison"
        && measurement.source !== "deterministic"
        && measurement.source !== "manual_override") continue;
      const desc = unitDescriptor(measurement.unit);
      if (!desc || (desc.family !== "mass" && desc.family !== "volume")) continue;
      if (!bridgeUsableMeasurement(measurement)) continue;
      const entry = {
        quantity: measurement.quantity / countQty,
        unit: measurement.unit,
        confirmed: measurement.confirmed === true,
        confidence: Number.isFinite(Number(measurement.confidence))
          ? Number(measurement.confidence) : 0.8,
        optionId: option.id != null ? String(option.id) : null,
        tier,
      };
      const bucket = byFamily.get(desc.family);
      if (bucket) bucket.push(entry);
      else byFamily.set(desc.family, [entry]);
    }
  }

  const ratios = [];
  for (const [family, allEntries] of byFamily) {
    // The strongest tier present defines the unit; weaker tiers are counts of
    // their own containers and neither define nor veto it.
    const bestTier = Math.min(...allEntries.map((entry) => entry.tier));
    const entries = allEntries.filter((entry) => entry.tier === bestTier);
    const head = entries[0];
    let agreed = true;
    for (const entry of entries.slice(1)) {
      const converted = convertBasisQuantity(entry.quantity, entry.unit, head.unit);
      if (converted === null || !sameQty(converted, head.quantity, RATIO_TOLERANCE)) {
        agreed = false;
        break;
      }
    }
    if (!agreed) continue; // conflicting definitions are a human question
    ratios.push({
      family,
      quantity: round6(head.quantity),
      unit: head.unit,
      confirmed: entries.every((entry) => entry.confirmed),
      confidence: Math.min(...entries.map((entry) => entry.confidence)),
      optionIds: [...new Set(entries.map((entry) => entry.optionId).filter(Boolean))],
    });
  }
  if (!ratios.length) return null;
  return { countUnit, ratios };
}

// Deterministic container arithmetic: the stored quantity IS the derivation
// (one case is one case; the pack counted 24 bottles), so a physical measure
// beside it is a simultaneous fact, never evidence the number was bridged.
const CONTAINER_ARITHMETIC_METHODS = new Set([
  "container_identity", "container_count", "piece_identity",
]);

const isUnsafeCrossDimensionStoredBridge = (basis, needUnit, measurements, option = null) => {
  if (basis?.source !== "stored_qty") return false;
  if (CONTAINER_ARITHMETIC_METHODS.has(text(option?.conversion_provenance?.method))) return false;
  const need = unitDescriptor(needUnit);
  if (!need) return true;
  return measurements.some((measurement) => {
    if (!["vendor_pack", "structured", "deterministic", "manual_override"].includes(measurement.source)
        && measurement.role !== "comparison") return false;
    const other = unitDescriptor(measurement.unit);
    return other && other.family !== need.family;
  });
};

/**
 * Resolves the quantity of one purchase package in the physical unit of NEED.
 * Package math is forbidden until this returns compatible=true.
 *
 * `needBridge` (itemCountUnitPhysicalBasis's result) lets a container-unit
 * need be satisfied through physical contents: with "one case = 384 fl oz" on
 * record, a 192 fl oz package IS 0.5 case for order math, even though nothing
 * on the package mentions cases. Without a bridge, container needs still
 * require the package to state the container itself.
 */
export function resolveNeedToPackageBasis({
  needQty,
  needUnit,
  option,
  packageBasis = null,
  item = null,
  needBridge = null,
} = {}) {
  const basis = packageBasis ?? resolveOrderPackageBasis(option, { item });
  const measurements = extractPackageMeasurements(option, { item });
  const requestedUnit = text(needUnit ?? item?.unit) || null;
  const need = positive(needQty) ?? (Number(needQty) === 0 ? 0 : null);
  const unresolved = {
    compatible: false,
    convertedNeedQty: null,
    packageQuantity: null,
    packageQuantityUnit: null,
    quantityPerPackageInNeedUnit: null,
    source: "unresolved",
    confidence: 0,
    warnings: [PACKAGE_BASIS_WARNINGS.NEED_UNIT_INCOMPATIBLE],
    measurements,
  };
  if (!option || !requestedUnit || need === null) return unresolved;

  const requestedDesc = unitDescriptor(requestedUnit);
  const candidates = [];
  measurements.forEach((measurement) => {
    if (!ORDERABLE_MEASUREMENT_SOURCES.has(measurement.source)) return;
    if (measurement.source === "ai_estimate" || measurement.source === "ai") return;
    const converted = convertBasisQuantity(measurement.quantity, measurement.unit, requestedUnit);
    if (converted === null) return;
    const explicitlyConfirmed = measurement.confirmed
      || CONFIRMED_MEASUREMENT_SOURCES.has(measurement.source);
    // A same-word container count must also be the same CONTAINER. When the
    // item's own packages define the count unit physically (needBridge) and
    // this package's exact contents imply a materially different size per
    // container, the words merely collide — 12 × 8.4 fl oz "cans" are not 12
    // of an item's 46 fl oz can. The physical-bridge rung below covers the
    // need honestly instead. A human confirmation still outranks this.
    if (!explicitlyConfirmed && requestedDesc?.family === "count"
      && containerCountContradictsBridge({ countQty: converted, measurements, needBridge })) return;
    candidates.push({ measurement, converted, explicitlyConfirmed });
  });

  // Human confirmation is the only thing that may establish a material
  // piece↔weight bridge. Prefer it over every cache/parser reading.
  let chosen = candidates.find((candidate) => candidate.explicitlyConfirmed);

  // The legal order unit is itself a deterministic container measurement.
  // This rung deliberately follows human confirmation and precedes imported
  // quantities: a confirmed answer wins, while an EACH-era import cannot make
  // one ordered CASE equal four needed CASES. If the conversion owner sees the
  // divided-case signature, stop — falling through to the stored 24 bottles
  // would authorize exactly the package it refused.
  const storedSameUnitIdentity = candidates.some((candidate) =>
    sameQty(candidate.converted, 1)
    && sameUnitIdentity(candidate.measurement.unit, requestedUnit));
  const purchaseIdentity = !chosen && !storedSameUnitIdentity
    ? purchaseUnitIdentityForNeed(option, requestedUnit)
    : null;
  if (!chosen && purchaseIdentity?.compatible) {
    chosen = {
      measurement: {
        quantity: 1,
        unit: requestedUnit,
        source: "purchase_unit_identity",
        confidence: 1,
      },
      converted: 1,
      explicitlyConfirmed: false,
      purchaseIdentity: true,
    };
  } else if (!chosen && purchaseIdentity?.ambiguous) {
    return unresolved;
  }

  if (!chosen) {
    const safePrimary = basis.quantityInInventoryUnit !== null
      && !isUnsafeCrossDimensionStoredBridge(basis, requestedUnit, measurements, option)
      // The fallback_each DEFAULT never resolves a need — absence of evidence
      // is not evidence of one.
      && !(basis.source === "stored_qty" && storedQtyIsFallbackDefault(option))
      ? convertBasisQuantity(basis.quantityInInventoryUnit, basis.inventoryUnit, requestedUnit)
      : null;
    // The same same-word/different-container guard as the candidate loop: a
    // stored "12 cans" beside exact contents of 8.4 fl oz each never covers a
    // 46 fl oz can need by word alone. Confirmed bases are a human's answer.
    if (safePrimary !== null && basis.source !== "confirmed"
      && requestedDesc?.family === "count"
      && containerCountContradictsBridge({ countQty: safePrimary, measurements, needBridge })) {
      chosen = null;
    } else if (safePrimary !== null) {
      chosen = {
        measurement: {
          quantity: basis.quantityInInventoryUnit,
          unit: basis.inventoryUnit,
          source: basis.source,
          confidence: basis.confidence,
        },
        converted: safePrimary,
        explicitlyConfirmed: basis.source === "confirmed",
      };
    }
  }

  if (!chosen) {
    // A deterministic vendor-pack/comparison measurement may satisfy a need
    // in its own dimension (50 LB package for an LB need), but it never
    // bridges an EA need into weight.
    chosen = candidates.find((candidate) =>
      ["vendor_pack", "structured", "deterministic", "manual_override"].includes(candidate.measurement.source)
      || candidate.measurement.role === "comparison") || null;
  }

  // ── The physical bridge ───────────────────────────────────────────────────
  // A container-unit need whose container the package never names can still
  // be covered through contents: need 0.8 case × 384 fl oz/case = 307.2 fl oz,
  // and a 192 fl oz package is 0.5 case of it. The bridge itself is derived
  // only from the item's own exact package facts (itemCountUnitPhysicalBasis),
  // so one vendor's case is never assumed to equal another's — both sides of
  // the division are stated measurements.
  let bridged = null;
  if (!chosen && needBridge && Array.isArray(needBridge.ratios)
    && sameUnitIdentity(needBridge.countUnit ?? requestedUnit, requestedUnit)) {
    outer: for (const ratio of needBridge.ratios) {
      if (!positive(ratio?.quantity) || !text(ratio?.unit)) continue;
      for (const measurement of measurements) {
        if (measurement.role === "comparison" && measurement.source !== "deterministic"
          && measurement.source !== "manual_override") continue;
        if (!bridgeUsableMeasurement(measurement)) continue;
        const packageInBridgeUnit = convertBasisQuantity(measurement.quantity, measurement.unit, ratio.unit);
        if (packageInBridgeUnit === null) continue;
        bridged = {
          measurement,
          converted: packageInBridgeUnit / ratio.quantity,
          explicitlyConfirmed: false,
          bridge: ratio,
        };
        break outer;
      }
    }
  }
  if (!chosen && bridged) chosen = bridged;
  if (!chosen) return unresolved;

  const source = chosen.explicitlyConfirmed
    ? (chosen.measurement.source === "manual_override" ? "manual_conversion" : "confirmed_conversion")
    : chosen.purchaseIdentity ? "purchase_unit_identity"
      : chosen.bridge ? "physical_bridge"
      : sameUnitIdentity(chosen.measurement.unit, requestedUnit) ? "same_unit" : "deterministic_conversion";
  return {
    compatible: true,
    convertedNeedQty: need,
    packageQuantity: round6(chosen.converted),
    packageQuantityUnit: requestedUnit,
    quantityPerPackageInNeedUnit: round6(chosen.converted),
    source,
    confidence: chosen.explicitlyConfirmed
      ? 1
      : chosen.bridge
        ? Math.min(chosen.measurement.confidence ?? 0.8, chosen.bridge.confidence ?? 0.8)
        : (chosen.measurement.confidence ?? basis.confidence ?? 0.8),
    warnings: [],
    measurements,
    ...(chosen.bridge
      ? {
        bridge: {
          countUnit: needBridge.countUnit ?? requestedUnit,
          quantity: chosen.bridge.quantity,
          unit: chosen.bridge.unit,
          family: chosen.bridge.family ?? null,
        },
      }
      : {}),
  };
}

// ── The canonical basis ──────────────────────────────────────────────────────

/**
 * What one orderable package IS, resolved from stored facts. Order of trust —
 * the same chain purchaseEconomics.resolvePackageQuantity established:
 *
 *   1. a human-confirmed package interpretation in conversion_snapshot;
 *   2. structured inner-pack columns (count × size);
 *   3. the stored qty_per_package column.
 *
 * Never re-parses pack text: parsing happens once, server-side, and is frozen
 * into snapshots. A parser improving must not change what a confirmed package
 * means.
 *
 * @returns {{
 *   version: number, purchaseOptionId: string|null,
 *   purchaseUnit: string|null, packageDescription: string|null,
 *   inventoryUnit: string|null, quantityInInventoryUnit: number|null,
 *   innerPackCount: number|null, innerPackUnit: string|null,
 *   quantityPerInnerPack: number|null, innerQuantityUnit: string|null,
 *   packagePrice: number|null, packagePriceBasis: 'one_purchase_unit',
 *   moqPackages: number|null, orderIncrement: number|null,
 *   source: 'confirmed'|'structured'|'stored_qty'|'unresolved',
 *   confidence: number, requiresReview: boolean,
 *   fingerprint: string
 * }}
 */
export function resolveOrderPackageBasis(option, { item = null } = {}) {
  const confirmed = confirmedInterpretationQty(option);
  const structured = structuredQtyOf(option);
  const storedQty = positive(option?.qty_per_package);
  const storedUnit = option?.normalized_count_unit ?? item?.unit ?? item?.count_unit ?? null;
  const confirmedUnit = confirmed?.unit ?? storedUnit;
  const structuredUnit = structured?.unit ?? storedUnit;
  const unitFallback = option?.normalized_count_unit ?? item?.unit ?? item?.count_unit ?? null;
  const measurements = extractPackageMeasurements(option, { item });
  const confirmedMeasurement = measurements.find((measurement) => {
    if (!measurement.confirmed || !unitFallback) return false;
    return convertBasisQuantity(measurement.quantity, measurement.unit, unitFallback) !== null;
  }) || null;

  let quantity = null;
  let unit = null;
  let source = "unresolved";
  let confidence = 0;
  let requiresReview = true;

  if (confirmedMeasurement) {
    quantity = convertBasisQuantity(confirmedMeasurement.quantity, confirmedMeasurement.unit, unitFallback);
    unit = unitFallback;
    source = "confirmed";
    confidence = 1;
    requiresReview = false;
  } else if (confirmed) {
    quantity = confirmed.quantity;
    unit = confirmed.unit ?? unitFallback;
    source = "confirmed";
    confidence = 1;
    requiresReview = false;
  } else if (structured) {
    quantity = structured.quantity;
    unit = structured.unit ?? unitFallback;
    source = "structured";
    confidence = 0.9;
    requiresReview = option?.conversion_snapshot?.package_interpretation?.requires_review === true;
  } else if (storedQty !== null) {
    const fallbackDefault = storedQtyIsFallbackDefault(option);
    quantity = storedQty;
    unit = unitFallback;
    source = "stored_qty";
    confidence = fallbackDefault ? 0.2 : 0.6;
    // A bare qty with no unit label is the historical hazard (the number may
    // be an inner-pack count) — reviewable, but not a conflict on its own.
    // A fallback-default 1 is always reviewable: nothing derived it.
    requiresReview = unitFallback == null || fallbackDefault;
  }

  const basisFacts = {
    purchaseUnit: option?.purchase_unit ?? null,
    quantityInInventoryUnit: quantity,
    inventoryUnit: unit,
    innerPackCount: structured?.count ?? positive(option?.inner_pack_count),
    quantityPerInnerPack: structured?.size ?? positive(option?.inner_pack_size),
    innerQuantityUnit: option?.inner_pack_size_unit ?? null,
  };

  return {
    version: PACKAGE_BASIS_VERSION,
    purchaseOptionId: option?.id != null ? String(option.id) : null,
    purchaseUnit: basisFacts.purchaseUnit,
    packageDescription: option?.pack_description ?? null,
    inventoryUnit: unit,
    quantityInInventoryUnit: quantity,
    innerPackCount: basisFacts.innerPackCount,
    innerPackUnit: option?.inner_pack_unit ?? null,
    quantityPerInnerPack: basisFacts.quantityPerInnerPack,
    innerQuantityUnit: basisFacts.innerQuantityUnit,
    packagePrice: positive(option?.package_price),
    packagePriceBasis: "one_purchase_unit",
    moqPackages: positive(option?.moq_packages),
    orderIncrement: positive(option?.order_increment),
    source,
    confidence,
    requiresReview,
    fingerprint: fingerprintOfFacts(basisFacts),
    measurements,
  };
}

// ── The fingerprint ──────────────────────────────────────────────────────────
//
// A canonical, readable key over the PACKAGE-DEFINING facts — deliberately not
// an opaque hash, following packageIdentityKey's precedent: a log line should
// explain itself. Included: the order unit, the resolved contents (quantity +
// unit) and the inner-pack structure. Excluded on purpose:
//
//   - price, MOQ, order increment  a price-only or terms-only update must not
//                                  invalidate a comparison of the same package
//   - pack_description raw text   cosmetic rewording ("4LB BG" → "4 lb bag")
//                                  is not a package change; a MATERIAL rewrite
//                                  reaches the fingerprint through the
//                                  structured facts it updates
//   - provenance                  confirming the same facts a parser derived
//                                  does not change what the package is

function fingerprintOfFacts(facts) {
  const parts = [
    `pb${PACKAGE_BASIS_VERSION}`,
    `u:${fold(facts.purchaseUnit)}`,
    `q:${facts.quantityInInventoryUnit == null ? "" : round6(facts.quantityInInventoryUnit)}`,
    `qu:${fold(facts.inventoryUnit)}`,
    `ic:${facts.innerPackCount == null ? "" : round6(facts.innerPackCount)}`,
    `is:${facts.quantityPerInnerPack == null ? "" : round6(facts.quantityPerInnerPack)}`,
    `iu:${fold(facts.innerQuantityUnit)}`,
  ];
  return parts.join("|");
}

/** The current fingerprint of an option's order package. */
export function packageBasisFingerprint(option, { item = null } = {}) {
  return resolveOrderPackageBasis(option, { item }).fingerprint;
}

// ── Comparison validation against the basis ──────────────────────────────────

/**
 * Whether a stored comparison record may speak for this option's CURRENT
 * package, and how firmly.
 *
 *   - A snapshot carrying a package_basis_fingerprint is PROVEN iff it
 *     matches the current fingerprint; a mismatch is STALE — the package it
 *     measured no longer exists on this row, and no arithmetic rescues it.
 *   - A legacy snapshot (no fingerprint) is validated where stored data can
 *     prove agreement: same-unit quantities must agree to
 *     QTY_TOLERANCE_EXACT; deterministically convertible units must agree to
 *     QTY_TOLERANCE_CONVERTED after conversion. A provable DISAGREEMENT is a
 *     contradiction and the record is unusable.
 *   - When agreement can be neither proven nor disproven — the order-side
 *     quantity is itself unresolved (the comparison is then the only package
 *     definition on record and contradicts nothing), or the two units need a
 *     bridge stored data cannot make (an AI piece↔weight estimate) — the
 *     record stays USABLE for display and vendor resolution but is flagged
 *     UNPROVEN: it may not crown Best Unit Price until a recompute stamps
 *     the current fingerprint.
 *
 * @param {object} option  purchase_options row
 * @param {object} stored  { quantity, relationshipUnit, source, confidence,
 *                           fingerprint } — storedComparisonFacts() shape, or
 *                           a reader's { quantityPerPurchasePackage, ... }
 * @returns {{ usable: boolean, proven: boolean, code: string|null, reason: string }}
 */
export function validateComparisonAgainstBasis(option, stored, { item = null, basis = null } = {}) {
  const facts = stored && typeof stored === "object"
    ? {
      quantity: positive(stored.quantity ?? stored.quantityPerPurchasePackage),
      relationshipUnit: stored.relationshipUnit ?? null,
      source: stored.source ?? null,
      fingerprint: text(stored.fingerprint ?? stored.packageBasisFingerprint) || null,
    }
    : null;
  if (!facts || facts.quantity === null) {
    return { usable: false, proven: false, code: null, reason: "no_stored_comparison" };
  }

  const resolved = basis ?? resolveOrderPackageBasis(option, { item });

  if (facts.fingerprint) {
    if (facts.fingerprint === resolved.fingerprint) {
      return { usable: true, proven: true, code: null, reason: "fingerprint_match" };
    }
    return {
      usable: false,
      proven: false,
      code: PACKAGE_BASIS_WARNINGS.COMPARISON_STALE,
      reason: "fingerprint_mismatch",
    };
  }

  // A package may carry more than one proven measurement (for example one
  // case = 80 EA and 50 LB). A legacy comparison is proven when it agrees
  // with any non-comparison measurement of that same package, not only the
  // primary inventory measurement.
  const matchingMeasurement = (resolved.measurements || []).find((measurement) => {
    if (measurement.role === "comparison") return false;
    if (measurement.source === "ai" || measurement.source === "ai_estimate") return false;
    const converted = convertBasisQuantity(
      measurement.quantity,
      measurement.unit,
      facts.relationshipUnit,
    );
    return converted !== null && sameQty(converted, facts.quantity, QTY_TOLERANCE_CONVERTED);
  });
  if (matchingMeasurement) {
    return { usable: true, proven: true, code: null, reason: "validated_package_measurement" };
  }

  // Legacy snapshot. With no order-side quantity there is no competing
  // package definition to contradict — and no order can be created from the
  // option either (it is not order-ready), so the comparison stands as the
  // only definition on record.
  if (resolved.quantityInInventoryUnit === null) {
    return {
      usable: true,
      proven: false,
      code: PACKAGE_BASIS_WARNINGS.COMPARISON_UNVALIDATED,
      reason: "basis_unresolved_no_claim",
    };
  }

  const inventoryUnit = resolved.inventoryUnit ?? item?.unit ?? null;
  const relDesc = unitDescriptor(facts.relationshipUnit);
  const invDesc = unitDescriptor(inventoryUnit);
  const sameUnit = fold(facts.relationshipUnit) !== ""
    && relDesc != null
    && invDesc != null
    && relDesc.family === invDesc.family
    && relDesc.key === invDesc.key;

  if (sameUnit) {
    if (sameQty(facts.quantity, resolved.quantityInInventoryUnit, QTY_TOLERANCE_EXACT)) {
      return { usable: true, proven: true, code: null, reason: "validated_same_unit" };
    }
    return {
      usable: false,
      proven: false,
      code: PACKAGE_BASIS_WARNINGS.COMPARISON_QUANTITY_CONFLICT,
      reason: "same_unit_quantity_disagreement",
    };
  }

  const converted = convertBasisQuantity(
    resolved.quantityInInventoryUnit,
    inventoryUnit,
    facts.relationshipUnit,
  );
  if (converted !== null) {
    if (sameQty(facts.quantity, converted, QTY_TOLERANCE_CONVERTED)) {
      return { usable: true, proven: true, code: null, reason: "validated_converted" };
    }
    return {
      usable: false,
      proven: false,
      code: PACKAGE_BASIS_WARNINGS.COMPARISON_QUANTITY_CONFLICT,
      reason: "converted_quantity_disagreement",
    };
  }

  // Units stored data cannot bridge (piece↔weight needs the AI's estimate,
  // which is exactly what this snapshot is). Not provable, not disprovable —
  // usable but unproven until a recompute stamps the fingerprint.
  return {
    usable: true,
    proven: false,
    code: PACKAGE_BASIS_WARNINGS.COMPARISON_UNVALIDATED,
    reason: "units_not_provable_from_stored_data",
  };
}

// ── The consistency validator ────────────────────────────────────────────────

/**
 * Cross-examines one option's stored facts and returns the two readiness
 * states everything downstream gates on.
 *
 *   orderReady       the option may create an order: its package is resolved,
 *                    the quantity is valid, the price is for one stated
 *                    purchase unit, MOQ/increment are valid, and no
 *                    package-definition conflict exists.
 *   comparisonReady  the option may hold Best Unit Price: it is order-ready
 *                    AND its stored comparison provably describes the SAME
 *                    package (current fingerprint, or validated agreement)
 *                    AND an AI-estimated comparison clears the confidence bar.
 *
 * status: 'ok' | 'needs_review' | 'conflict' | 'unresolved'. A conflict —
 * two stored facts describing different packages — is never orderable; a
 * suspicious-but-uncorroborated pattern is needs_review and stays orderable,
 * because arithmetic alone must not rewrite what a vendor sells (source
 * evidence or a human confirmation repairs it).
 *
 * @param {object} option
 * @param {object} [ctx] { item, comparisonEntry, needBridge } —
 *   comparisonEntry is an assembled-comparison entry for this option when the
 *   caller has one; otherwise the option's own stored snapshot is read.
 *   needBridge is the item's count-unit physical definition
 *   (itemCountUnitPhysicalBasis) when the caller has the item's own options;
 *   with it, a package measured only in fl oz can be order-ready for a
 *   case-counted need.
 */
export function validatePackageConsistency(option, { item = null, comparisonEntry = null, needBridge = null } = {}) {
  const warnings = [];
  const details = [];
  const addDetail = (code, message, expected = null, actual = null) => {
    if (!warnings.includes(code)) warnings.push(code);
    details.push({ code, message, expected, actual });
  };

  const basis = resolveOrderPackageBasis(option, { item });
  const confirmed = confirmedInterpretationQty(option);
  const structured = structuredQtyOf(option);
  const storedQty = positive(option?.qty_per_package);
  const storedUnit = option?.normalized_count_unit ?? item?.unit ?? item?.count_unit ?? null;
  const confirmedUnit = confirmed?.unit ?? storedUnit;
  const structuredUnit = structured?.unit ?? storedUnit;
  const price = positive(option?.package_price);
  const normalizedCost = positive(option?.normalized_unit_cost);
  const hasPrice = price !== null || normalizedCost !== null;

  let conflict = false;
  const semantic = option?.conversion_snapshot?.package_interpretation;
  const semanticConfirmed = semantic && typeof semantic === "object"
    && (semantic.source === "confirmed" || Boolean(semantic.confirmed_at));
  const semanticReviewRequired = semantic && typeof semantic === "object"
    && !semanticConfirmed
    && (semantic.state === "conflict"
      || semantic.trust_decision === "confirm_suggestion"
      || semantic.trust_decision === "manual_required"
      // Backward compatibility for an older AI-only record that predates the
      // explicit three-way trust disposition.
      || (semantic.state === "ai_resolved" && semantic.trust_decision !== "auto_accept"));
  if (semanticReviewRequired) {
    addDetail(
      PACKAGE_BASIS_WARNINGS.SEMANTIC_REVIEW_REQUIRED,
      semantic.state === "conflict"
        ? "The supplier wording and saved package facts disagree."
        : "Countertop has a likely package reading that still needs confirmation.",
    );
  }

  // ── The package itself ────────────────────────────────────────────────────
  if (!text(option?.purchase_unit)) {
    addDetail(
      PACKAGE_BASIS_WARNINGS.MISSING_PURCHASE_UNIT,
      "No purchase unit is stated, so the price has no stated package to belong to.",
    );
  }
  if (!hasPrice) {
    addDetail(
      PACKAGE_BASIS_WARNINGS.MISSING_PRICE_BASIS,
      "No price is stored for this package.",
    );
  }
  // A package with no direct quantity in the item's unit can still be fully
  // resolved live, two ways. THROUGH the item's physical definition of that
  // unit (the 192 fl oz package of a 384 fl oz/case item IS 0.5 case), or by
  // the legal order unit itself satisfying a container need — purchase unit
  // "case" over a case-counted shelf is 1 by definition, re-derived through
  // the one conversion owner (with its divided-price refusal intact). The
  // identity rung matters for rows whose STORED refusal predates the
  // identity-is-order-safe rule: the row's own facts already answer, and the
  // fan-out SQL gate (package_basis_for_need) resolves the same rung, so
  // refusing here only stranded the row in a repair card the server would
  // have accepted. Every other historical refusal keeps its exact meaning.
  const needUnitEarly = item?.unit ?? item?.count_unit ?? option?.normalized_count_unit ?? null;
  const needProbe = needUnitEarly
    ? resolveNeedToPackageBasis({ needQty: 1, needUnit: needUnitEarly, option, packageBasis: basis, item, needBridge })
    : null;
  const bridgeResolved = basis.quantityInInventoryUnit === null
    && needProbe?.compatible === true
    && (needProbe.source === "physical_bridge"
      || needProbe.source === "purchase_unit_identity");
  if (basis.quantityInInventoryUnit === null && !bridgeResolved) {
    addDetail(
      PACKAGE_BASIS_WARNINGS.UNRESOLVED_PACKAGE_QUANTITY,
      "The package does not say how much it contains in the item's unit.",
    );
  } else if (basis.source === "stored_qty" && basis.requiresReview) {
    addDetail(
      PACKAGE_BASIS_WARNINGS.UNLABELLED_QUANTITY,
      "The stored quantity carries no unit label; it may be an inner-pack count.",
      null,
      basis.quantityInInventoryUnit,
    );
  }

  // ── Do the stored facts agree with each other? ────────────────────────────
  const structuredStoredAgreement = structured && storedQty !== null
    ? measurementsAgree(structured.quantity, structuredUnit, storedQty, storedUnit)
    : null;
  // Structured inner-pack contents and the stored inventory quantity may use
  // different dimensions only when a human has explicitly confirmed the
  // bridge. Otherwise "8 bags x 12 buns" beside qty_per_package=1 bag is the
  // historical prorated-package contradiction, not two trustworthy measures.
  if (structuredStoredAgreement !== true && structured && storedQty !== null) {
    if (confirmed) {
      // Different physical dimensions may both describe the same package once
      // a human bridge exists (for example 50 LB and 80 EA per case). Only a
      // same-dimension numeric disagreement makes the losing cache stale.
      if (structuredStoredAgreement === false) {
        addDetail(
          PACKAGE_BASIS_WARNINGS.STORED_QTY_STALE,
          "qty_per_package disagrees with the confirmed interpretation and should be healed to it.",
          confirmed.quantity,
          storedQty,
        );
      }
    } else {
      // The piece-count model is NOT the prorated-package corruption: for a
      // count-family item, "20/12 OZ" stores qty_per_package = 20 (pieces)
      // while the structured columns size ONE PIECE (20 × 12 oz). The stored
      // count EQUALS the inner-pack count on purpose, and a deterministic
      // piece_count measurement pins it. Only that exact shape is exempt.
      const storedDesc = unitDescriptor(storedUnit);
      const pieceCountMeasurement = (basis.measurements || []).find((m) =>
        m.role === "piece_count"
        && (m.source === "deterministic" || m.confirmed === true)
        && sameQty(m.quantity, storedQty));
      const legitimatePieceCount = storedDesc?.family === "count"
        && positive(option?.inner_pack_count) !== null
        && sameQty(storedQty, option.inner_pack_count)
        && pieceCountMeasurement != null;
      // Container identity is the other legitimate cross-dimension shape: a
      // case-counted item stores qty 1 (one case IS one case) while the
      // structured columns describe the case's CONTENTS (6 × 8 oz). Both are
      // true; no human bridge is needed for identity.
      const legitimateContainerIdentity = storedDesc?.family === "count"
        && sameQty(storedQty, 1)
        && text(option?.conversion_provenance?.method) === "container_identity";
      if (!legitimatePieceCount && !legitimateContainerIdentity) {
        conflict = true;
        addDetail(
          PACKAGE_BASIS_WARNINGS.PACKAGE_BASIS_MISMATCH,
          "The structured inner-pack total and qty_per_package describe different packages.",
          structured.quantity,
          storedQty,
        );
        if (positive(option?.inner_pack_count) !== null
          && sameQty(storedQty, option.inner_pack_count)) {
          addDetail(
            PACKAGE_BASIS_WARNINGS.QTY_MATCHES_INNER_PACK_COUNT,
            "qty_per_package equals the inner-pack COUNT, not the package's total contents.",
            structured.quantity,
            storedQty,
          );
        }
      }
    }
  }
  const confirmedStoredAgreement = confirmed && storedQty !== null
    ? measurementsAgree(confirmed.quantity, confirmedUnit, storedQty, storedUnit)
    : null;
  if (confirmedStoredAgreement === false
    && !warnings.includes(PACKAGE_BASIS_WARNINGS.STORED_QTY_STALE)) {
    addDetail(
      PACKAGE_BASIS_WARNINGS.STORED_QTY_STALE,
      "qty_per_package disagrees with the confirmed interpretation and should be healed to it.",
      confirmed.quantity,
      storedQty,
    );
  }
  const confirmedStructuredAgreement = confirmed && structured
    ? measurementsAgree(confirmed.quantity, confirmedUnit, structured.quantity, structuredUnit)
    : null;
  if (confirmedStructuredAgreement === false) {
    addDetail(
      PACKAGE_BASIS_WARNINGS.STRUCTURED_FIELDS_STALE,
      "The structured inner-pack columns disagree with the confirmed interpretation.",
      confirmed.quantity,
      structured.quantity,
    );
  }

  // ── Order constraints ─────────────────────────────────────────────────────
  const moqRaw = option?.moq_packages;
  if (moqRaw !== null && moqRaw !== undefined && moqRaw !== "" && positive(moqRaw) === null) {
    addDetail(PACKAGE_BASIS_WARNINGS.INVALID_MOQ, "The stated MOQ is not a positive number.", null, moqRaw);
  }
  const incRaw = option?.order_increment;
  if (incRaw !== null && incRaw !== undefined && incRaw !== "" && positive(incRaw) === null) {
    addDetail(
      PACKAGE_BASIS_WARNINGS.INVALID_ORDER_INCREMENT,
      "The stated order increment is not a positive number.",
      null,
      incRaw,
    );
  }

  // If the vendor bills one orderable sack/case/bag, purchase_unit must name
  // that object. A row saying purchase_unit=LB while the package description
  // and quantity say "25 LB SACK" would otherwise render $28 per LB and use
  // one "LB" as the legal package count. This is a deterministic conflict,
  // not a label-only problem.
  const purchaseUnitDescriptor = unitDescriptor(option?.purchase_unit);
  const wrapperInDescription = fold(option?.pack_description).match(
    /(?:^| )(case|bag|sack|box|carton|pail|tub|jug|bottle|can|bucket)(?: |$)/,
  )?.[1] ?? null;
  if (purchaseUnitDescriptor
      && ["mass", "volume"].includes(purchaseUnitDescriptor.family)
      && wrapperInDescription
      && option?.allows_fractional_packages !== true
      && basis.quantityInInventoryUnit !== null
      && basis.quantityInInventoryUnit > 1) {
    conflict = true;
    addDetail(
      PACKAGE_BASIS_WARNINGS.PURCHASE_UNIT_PACKAGE_CONFLICT,
      `The price is attached to one ${wrapperInDescription}, but purchase_unit says ${option.purchase_unit}.`,
      wrapperInDescription,
      option.purchase_unit,
    );
  }

  // ── Derived cost sanity ───────────────────────────────────────────────────
  if (price !== null && normalizedCost !== null && basis.quantityInInventoryUnit !== null) {
    const derived = price / basis.quantityInInventoryUnit;
    if (!sameQty(derived, normalizedCost, NORMALIZED_COST_TOLERANCE)) {
      addDetail(
        PACKAGE_BASIS_WARNINGS.NORMALIZED_COST_MISMATCH,
        "normalized_unit_cost disagrees with package_price ÷ package quantity.",
        round6(derived),
        normalizedCost,
      );
    }
  }

  // ── The stored comparison against this package ────────────────────────────
  const comparisonFacts = comparisonEntry
    ? {
      quantity: positive(comparisonEntry.quantityPerPurchasePackage ?? comparisonEntry.quantity),
      relationshipUnit: comparisonEntry.relationshipUnit ?? comparisonEntry.relationship_unit ?? null,
      source: comparisonEntry.source ?? null,
      confidence: Number.isFinite(Number(comparisonEntry.confidence)) ? Number(comparisonEntry.confidence) : null,
      fingerprint: text(comparisonEntry.packageBasisFingerprint ?? comparisonEntry.package_basis_fingerprint) || null,
    }
    : storedComparisonFacts(option);

  let comparisonUsable = false;
  let comparisonProven = false;
  if (comparisonFacts && comparisonFacts.quantity !== null) {
    const verdict = validateComparisonAgainstBasis(option, comparisonFacts, { item, basis });
    if (verdict.usable) {
      comparisonUsable = true;
      comparisonProven = verdict.proven;
      if (!verdict.proven && verdict.code) {
        addDetail(
          verdict.code,
          "The stored comparison cannot be proven to describe the current package; a recompute will stamp the current fingerprint.",
          basis.fingerprint,
          comparisonFacts.fingerprint,
        );
      }
      if (comparisonFacts.source === "ai_estimate") {
        const conf = comparisonFacts.confidence;
        if (!Number.isFinite(conf) || conf < BASIS_AI_CONFIDENCE_THRESHOLD) {
          comparisonProven = false;
          addDetail(
            PACKAGE_BASIS_WARNINGS.COMPARISON_LOW_CONFIDENCE,
            "The comparison is an AI estimate below the confidence bar; it may display but not pick winners.",
            BASIS_AI_CONFIDENCE_THRESHOLD,
            conf,
          );
        }
      }
    } else if (verdict.code === PACKAGE_BASIS_WARNINGS.COMPARISON_QUANTITY_CONFLICT) {
      // Two stored engines measured the SAME package and disagree. When the
      // disagreeing measurement is deterministic (a parse of the vendor's own
      // pack text) or human-entered, the package definition itself is in
      // dispute — the screenshot bug. An AI estimate disagreeing is only a
      // comparison problem: an estimate must not invalidate the package.
      addDetail(
        verdict.code,
        "The stored comparison quantity and the order package quantity describe different packages.",
        basis.quantityInInventoryUnit,
        comparisonFacts.quantity,
      );
      if (comparisonFacts.source === "deterministic" || comparisonFacts.source === "manual_override") {
        conflict = true;
        addDetail(
          PACKAGE_BASIS_WARNINGS.PACKAGE_BASIS_MISMATCH,
          "Order math and comparison math are using different definitions of one package.",
          basis.quantityInInventoryUnit,
          comparisonFacts.quantity,
        );
      }
    } else if (verdict.code) {
      addDetail(
        verdict.code,
        verdict.code === PACKAGE_BASIS_WARNINGS.COMPARISON_STALE
          ? "The stored comparison was computed against a previous package definition."
          : "The stored comparison cannot be proven to describe the current package.",
        basis.fingerprint,
        comparisonFacts.fingerprint,
      );
    }
  }

  const quantityResolved = basis.quantityInInventoryUnit !== null || bridgeResolved;
  // A missing purchase-unit WORD degrades display, not arithmetic — it stays
  // a review warning rather than an order blocker, or every legacy row with a
  // blank label would become unbuyable overnight. The hard gates are the
  // facts an order genuinely depends on: one non-contradictory package, a
  // resolved quantity (direct, through the item's physical definition of its
  // count unit, or by purchase-unit identity), a price, and sane
  // MOQ/increment.
  const packageReady = !conflict
    && !semanticReviewRequired
    && quantityResolved
    && hasPrice
    && !warnings.includes(PACKAGE_BASIS_WARNINGS.INVALID_MOQ)
    && !warnings.includes(PACKAGE_BASIS_WARNINGS.INVALID_ORDER_INCREMENT);
  // normalized_count_unit is the legacy row's statement of the inventory
  // dimension. Use it when the caller has not loaded the item so every gate
  // (including listAllocatableOptions) reaches the same compatibility verdict.
  const needUnit = needUnitEarly;
  const needBasis = packageReady && needUnit ? needProbe : null;
  if (needBasis && !needBasis.compatible) {
    addDetail(
      PACKAGE_BASIS_WARNINGS.NEED_UNIT_INCOMPATIBLE,
      `The item's need is in ${needUnit}, but no verified measurement of one ${basis.purchaseUnit || 'package'} is available in that unit.`,
      needUnit,
      basis.measurements,
    );
  }
  const orderReady = packageReady && (!needBasis || needBasis.compatible);

  // Best Unit Price demands proof: the comparison must reference the current
  // package (fingerprint, or validated agreement) — an unproven legacy
  // snapshot may display and resolve a vendor, but it crowns no winner.
  const comparisonReady = orderReady && comparisonUsable && comparisonProven;

  let status = "ok";
  if (conflict) status = "conflict";
  else if (!quantityResolved) status = "unresolved";
  else if (warnings.length > 0) status = "needs_review";

  return {
    status,
    packageReady,
    orderReady,
    comparisonReady,
    comparisonUsable,
    warnings,
    details,
    basis,
    needBasis,
  };
}

/**
 * The one question every ordering gate asks. True when the option's stored
 * facts describe DIFFERENT packages — such an option must never be selected,
 * badged, totalled, allocated, fanned out, or listed as buyable.
 */
export function hasPackageBasisConflict(option, ctx = {}) {
  return validatePackageConsistency(option, ctx).status === "conflict";
}

/** True when the option may create an order (spec §7 ORDER READY). */
export function isOrderReadyOption(option, ctx = {}) {
  return validatePackageConsistency(option, ctx).orderReady;
}
